
import { Component, Mixins } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { BvModalEvent } from "bootstrap-vue";
import mixin from "./mixin";
import * as modals from "@/lib/modals";
import notifications from "@/lib/notifications";
import OrderModule from "@/store/modules/order";
import { formatDateWithTime } from "@/utils";
import RightClickMenu from "@/components/widgets/RightClickMenu.vue";

const orderModule = getModule(OrderModule);

interface DocumentType{
  type: string;
  name: string;
}

@Component({
  name: modals.imagesModal,
  components: {
    RightClickMenu
  }
})
export default class ImagesModal extends Mixins(mixin) {
  loadingComplete: boolean = false;
  images: any[];
  selectedImages: string[] = [];
  imagesRotate: any[] = []
  imageStyle: string = "";
  imageContainerStyle: string = "";
  imageType: string = "";
  imageDate: string = "";
  imageIndex: number = 0;
  downloading: boolean = false;
  orderModule = getModule(OrderModule);
  mainImageUrl: string = "";
  mainImageLoadingComplete: boolean = false;
  rightClickLocation: Object = { x: 0, y: 0 };
  menuItems: any[] = [];
  showDeleteNotice: boolean = false;
  showReassign: boolean = false;
  destinationOrderNumber: number = null;
  reassignImageType: string = "";
  documentTypes: DocumentType[] = [
    { type: "UN", name: "Unattended" },
    { type: "UNS", name: "Manual Scan" },
    { type: "POD", name: "Proof of Delivery" },
    { type: "ORD", name: "Attended Delivery" },
    { type: "BOL", name: "Bill of Lading" },
    { type: "AT", name: "Attempt" },
    { type: "DMR", name: "Damaged Item Receiving" }
  ];

  get currentOrder() {
    return orderModule.currentOrder;
  }

  clickMenu(menuItem) {
    if(menuItem.imageType) {
      menuItem.itemAction(menuItem.imageType);
    } else {
      menuItem.itemAction();
    }
    this.clearRightClick();
  }

  clearRightClick() {
    this.rightClickLocation = null;
  }

  onRightClicked(event) {
    event.preventDefault();
    this.rightClickLocation = { x: event.x, y: event.y };
  }

  get image() {
    if(!this.images) return null;
    this.mainImageUrl = this.images[this.imageIndex].imageUrl;
    return this.images[this.imageIndex];
  }

  calculateRotate() {
    const angle = this.imagesRotate[this.imageIndex];
    let location = {x: 0, y: 0};
    var displayImage = this.$refs.displayImage;

    if(angle === 90 || angle === 270) {
        const x = Math.round((displayImage["clientHeight"] - displayImage["clientWidth"]) / 2);
        const y =  Math.round((displayImage["clientWidth"] - displayImage["clientHeight"]) / 2);
        location = {x: x, y: y};
    }

    this.imageStyle = `transform: translate(${location.x}px, ${location.y}px) rotate(${this.imagesRotate[this.imageIndex]}deg);`;
  }

  rotateLeft(){
    this.imagesRotate[this.imageIndex] = this.imagesRotate[this.imageIndex] === 0 ? 270 : this.imagesRotate[this.imageIndex] - 90;
    this.calculateRotate();
  }

  rotateRight(){
    this.imagesRotate[this.imageIndex] = this.imagesRotate[this.imageIndex] === 270 ? 0 : this.imagesRotate[this.imageIndex] + 90;
    this.calculateRotate();
  }

  modalClick() {
    this.clearRightClick();
  }

  formatImageId(image) {
    return `${image.source}-${image.id}-${image.page}`;
  }

  mounted() {
    this.$nextTick(() => {
      window.addEventListener('keyup', this.handleKeyUp);
    })
  }

  beforeDestroy() {
    window.removeEventListener('keyup', this.handleKeyUp)
  }

  load() {
    this.images = this.orderModule.currentOrderThumbnails;
    this.selectedImages = [];
    this.imageIndex = this.orderModule.currentOrderImageIndex
    this.imageType = this.image.type;
    this.imageDate = formatDateWithTime(this.image.date);
    this.loadingComplete = true;
    this.buildContextMenu();

    this.imagesRotate.length = 0;
    this.images.forEach(() => {
      this.imagesRotate.push(0);
    });
    this.setMainImageUrl(this.images[this.imageIndex]);
  }

  unload() {
    this.loadingComplete = false;
  }

  buildContextMenu() {
    this.menuItems.length = 0;
    this.menuItems.push(
      {
      title: "Select All",
      name: "selectAll",
      action: "selectAll",
      itemAction: this.selectAll
    });
    this.menuItems.push(
      {
      title: "Select None",
      name: "selectNone",
      action: "selectNone",
      itemAction: this.selectNone
    });

    const imageTypes = this.images.map(x => x).filter(this.distinct);
    imageTypes.forEach(imageType => {
      if(!this.menuItems.find(x => x.imageType == imageType.type)) {
        this.menuItems.push(
          {
            title: `Select All ${imageType.description}`,
            name: `selectAll${imageType.type}`,
            action: `selectAll${imageType.type}`,
            itemAction: this.selectAllImageType,
            imageType: imageType.type
          });
      }
    });
  }

  get downloadTitle() {
    if(this.selectedImages.length === 0) return "Download all pages as a PDF";
    if(this.selectedImages.length === 1) return "Download 1 page as a PDF";
    return `Download ${this.selectedImages.length} pages as a PDF`;
  }

  get messageTitle(){
    if(this.selectedImages.length === 0) return "Send all pages as a PDF";
    if(this.selectedImages.length === 1) return "Send 1 page as a PDF";
    return `Send ${this.selectedImages.length} pages as a PDF`;
  }

  handleKeyUp(e) {
    const keyCode = String(e.keyCode || e.code || e.keyIdentifier);
    if(keyCode === "39") this.next();
    if(keyCode === "37") this.previous();
  }

  async setImageIndex(imageIndex: number) {
    this.imageIndex = imageIndex;
    this.imageType = this.image.type;
    this.imageDate = formatDateWithTime(this.image.date);
    this.setMainImageUrl(this.image);
    this.$refs[`image${this.imageIndex}`][0].scrollIntoView({ behavior: 'smooth' });
  }

  setMainImageUrl(image) {
    this.mainImageLoadingComplete = false;
    this.imageStyle = `transform: rotate(${this.imagesRotate[this.imageIndex]}deg)`;
    if(image.imageUrl) {
      this.mainImageUrl = image.imageUrl;
      this.mainImageLoadingComplete = true;
    } else {
      let payload = {
        source: image.source,
        id: image.id,
        page: image.page
      }

      this.orderModule.getImage(payload)
        .then(result =>  {
          this.mainImageUrl = result.url;
          this.mainImageLoadingComplete = true;
        })
        .catch((error) => {
          notifications.error("Load Failed", "Unable to load image.", error);
          this.mainImageLoadingComplete = true;
        })
    }

  }

  imageClicked(imageIndex: number, event) {
    this.setImageIndex(imageIndex);
  }

  getSelectedClass(imageIndex: number) {
    return imageIndex === this.imageIndex ? "selected mb-1 mt-1" : "mb-1 mt-1";
  }

  previous() {
    if(this.imageIndex > 0) this.setImageIndex(this.imageIndex - 1);
  }

  next() {
    if(this.images && (this.imageIndex < this.images.length-1)) this.setImageIndex(this.imageIndex + 1);
  }

  downloadUrl(url: string, name: string) {
    var link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = name;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
        link.parentNode.removeChild(link);
    }, 0);
  }

  downloadImage(){
    if(this.downloading) {
      return;
    }

    this.downloading = true;
    notifications.display("Download Started", "Downloading single page.", "info");

    var image = this.images[this.imageIndex];
    if(image.imageUrl) {
      let fileName = `bc-${image.id}-${image.page}.jpg`
      this.downloadUrl(image.imageUrl, fileName);
      notifications.success("Download Complete", "Page downloaded.");
      this.downloading = false;
    } else {
      let payload = {
        source: image.source,
        id: image.id,
        page: image.page
      }

      this.orderModule.getImage(payload)
        .then(result =>  {
          this.downloadUrl(result.url, result.fileName);
          notifications.success("Download Complete", "Page downloaded.");
        })
        .catch((error) => {
          notifications.error("Download Failed", "Unable to download page.", error);
        })
        .finally(() => {
          this.downloading = false;
        });
    }
  }

  downloadImageBundle() {
    if(!this.downloading) {
      this.downloading = true;
      notifications.display("Download Started", "Downloading all order images.", "info");

      const payload = {
        orderNumber: this.orderModule.currentOrder.headerNumber,
        documentIds: this.selectedImages
      };

      this.orderModule.getOrderImageBundle(payload)
        .then(result => {
          var file = new Blob([result], {type: "application/pdf"});
          var link = document.createElement("a");
          link.href = URL.createObjectURL(file);
          link.download = `order-${this.orderModule.currentOrder.headerNumber}.pdf`;
          document.body.appendChild(link);
          link.click();

          notifications.success("Download Complete", "Order images downloaded.");

          setTimeout(() => {
            link.parentNode.removeChild(link);
          }, 0);
        })
        .catch(error => {
          notifications.error("Download Failed", "Unable to download orders.", error);
        })
        .finally(() => {
          this.downloading = false;
      });
    }
  }

  async deleteSelectedDocuments () {
    const payload = {
        orderNumber: this.orderModule.currentOrder.headerNumber,
        documentIds: this.selectedImages
      };

    await this.orderModule.removeOrderDocuments(payload);
    await this.orderModule.loadOrderImages(this.orderModule.currentOrder);
    await this.orderModule.loadOrderThumbnails(this.orderModule.currentOrder);
    this.hideModal();
  }

  async reassignDocuments () {
    if (!this.destinationOrderNumber || !this.reassignImageType) {
      return;
    }

    const payload = {
        documentIds: this.selectedImages,
        targetOrderNumber: this.orderModule.currentOrder.headerNumber,
        destinationOrderNumber: this.destinationOrderNumber,
        documentType: this.reassignImageType
      };

    this.hideModal();

    await this.orderModule.reassignOrderDocuments(payload);
    await this.orderModule.loadOrderImages(this.orderModule.currentOrder);
    await this.orderModule.loadOrderThumbnails(this.orderModule.currentOrder);
    this.destinationOrderNumber = null;
  }

  showDeleteModal (event: BvModalEvent) {
    if (this.selectedImages.length === 0) {
      event.preventDefault();
      notifications.error("No documents selected", "Select or more documents to delete.");
      return;
    }
  }

  showReassignModal (event: BvModalEvent) {
    if (this.selectedImages.length === 0) {
      event.preventDefault();
      notifications.error("No documents selected", "Select or more documents to reassign.");
      return;
    }

    let firstSelectedImage = this.images.find(x => this.formatImageId(x) === this.selectedImages[0]);
    this.reassignImageType = firstSelectedImage.type ?? null;
  }

  sendMessage() {
    var payload = {
      selectedItems: [this.orderModule.currentOrder, this.selectedImages],
    };
    this.$store.dispatch("order/messageCustomerShortcutAction", payload, { root: true });
  }

  getImageTitle(image) {
    let type = image.description;
    let datetime = formatDateWithTime(image.date);
    return `${type} (${datetime})`
  }

  getImageRef(index: number) {
    return `image${index}`;
  }

  selectAll() {
    this.selectedImages = this.images.map(x => this.formatImageId(x));
  }

  selectAllImageType(imageType) {
    const targetImages = this.images.filter(image => image.type === imageType && !this.selectedImages.includes(this.formatImageId(image)));
    targetImages.forEach(image => this.selectedImages.push(this.formatImageId(image)));
  }

  selectNone() {
    this.selectedImages = [];
  }

  distinct(value, index, self) {
    return self.indexOf(value) === index;
  }
}
